import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  createChecklistTemplate,
  createProjectTemplate,
  deleteChecklistTemplate,
  deleteProjectTemplate,
  listTemplates,
  updateChecklistTemplate,
  updateProjectTemplate,
  type ChecklistTemplate,
  type ProjectTemplate,
  type TemplatesView,
} from '../api.js';
import { RowActions } from './register-kit.js';

/**
 * Templates — written by the Company Admin, or not at all.
 *
 * A template carries the SHAPE of a thing: milestone offsets for a new
 * project, checklist lines for a QA walk. Applying one COPIES, so editing a
 * template never changes a project or an inspection that already exists —
 * the screen says so, because that is the question everyone asks.
 *
 * **There are no presets** (2026-08-17). `J1` step 3 seeded three milestone
 * sets and four ITP checklists into every new company; the user's decision was
 * *"no need for templates it should be manual"*, so the register starts empty
 * and stays that way until somebody writes one.
 */

interface Props {
  readonly canManage: boolean;
}

const PROJECT_TYPES = ['office', 'retail', 'residential', 'hospitality', 'other'] as const;

/** `14, Design sign-off *` → an offset, a name, and criticality. */
function parseMilestoneLines(
  raw: string,
): readonly { name: string; offset_days: number; is_critical: boolean }[] | string {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const parsed: { name: string; offset_days: number; is_critical: boolean }[] = [];
  for (const line of lines) {
    const match = /^(\d{1,4})\s*[,|]\s*(.+?)(\s*\*)?$/.exec(line);
    if (!match) {
      return `Could not read "${line}". Each line is: days, name — with a trailing * for critical.`;
    }
    parsed.push({
      name: match[2]!.trim(),
      offset_days: Number(match[1]),
      is_critical: Boolean(match[3]),
    });
  }
  return parsed;
}

const milestonesToLines = (t: ProjectTemplate): string =>
  t.milestones
    .map((m) => `${m.offset_days}, ${m.name}${m.is_critical ? ' *' : ''}`)
    .join('\n');

export function Templates({ canManage }: Props): JSX.Element {
  const [view, setView] = useState<TemplatesView | null>(null);
  /** A denied read is not an empty list and not a failure. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<ChecklistTemplate | null>(null);
  const [editingProject, setEditingProject] = useState<ProjectTemplate | null>(null);
  const [addingChecklist, setAddingChecklist] = useState(false);
  const [addingProject, setAddingProject] = useState(false);

  const load = useCallback(async () => {
    try {
      setView(await listTemplates());
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the templates.');
      setView({ checklist: [], project: [] });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<TemplatesView | null>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const next = await work();
      if (next) setView(next);
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Templates</h3>
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include your company&rsquo;s templates.</p>
      </>
    );
  }

  if (view === null) {
    return (
      <>
        {head}
        <p aria-live="polite">Loading the templates…</p>
      </>
    );
  }

  const empty = view.project.length === 0 && view.checklist.length === 0;

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      <p className="hint">
        Applying a template COPIES it — editing one here never changes a project or an
        inspection that already exists.
      </p>

      {/*
        No "add the starting sets" button any more, and no presets behind it.
        The user's decision on 2026-08-17: *"no need for templates it should be
        manual"*. The empty state names what a reader can actually do — write
        one — and says templates are optional, because they are: picking one on
        a new project has always been a choice, and nothing here constrains a
        job afterwards.
      */}
      {empty && (
        <div className="empty">
          <p>
            <strong>No templates.</strong>
          </p>
          <p>
            {canManage
              ? 'Templates are optional. If your projects repeat the same milestone set, save it here once and you can start a new job from it — otherwise type the milestones on the project itself.'
              : 'Templates are optional, and your company has not written any. Milestones and inspection checklists are entered on the project itself.'}
          </p>
        </div>
      )}

      {view.project.length > 0 && (
        <>
          <h4 className="sub-head">Project templates</h4>
          <table className="grid">
            <caption className="visually-hidden">Project templates</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Type</th>
                <th scope="col">Milestones</th>
                <th scope="col">Offered</th>
                {canManage && (
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {view.project.map((template) => (
                <tr key={template.id}>
                  <th scope="row">{template.name}</th>
                  <td data-label="Type">{template.project_type}</td>
                  <td data-label="Milestones">{template.milestones.length}</td>
                  <td data-label="Offered">{template.is_active ? 'Yes' : 'Switched off'}</td>
                  {canManage && (
                    <td>
                      {/*
                        No toolbar on this screen — a company writes a handful
                        of templates and a search box over four rows is
                        furniture. The row actions still follow the pattern:
                        Edit on the row, Remove behind the menu.
                      */}
                      <RowActions
                        rowLabel={template.name}
                        primary={{
                          label: 'Edit',
                          disabled: busy,
                          onSelect: () => setEditingProject(template),
                        }}
                        more={[
                          {
                            label: 'Remove',
                            danger: true,
                            disabled: busy,
                            onSelect: () => void run(() => deleteProjectTemplate(template.id)),
                          },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {view.checklist.length > 0 && (
        <>
          <h4 className="sub-head">Inspection checklists</h4>
          <table className="grid">
            <caption className="visually-hidden">Checklist templates</caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Discipline</th>
                <th scope="col">Lines</th>
                <th scope="col">Offered</th>
                {canManage && (
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {view.checklist.map((template) => (
                <tr key={template.id}>
                  <th scope="row">{template.name}</th>
                  <td data-label="Discipline">{template.discipline}</td>
                  <td data-label="Lines">{template.items.length}</td>
                  <td data-label="Offered">{template.is_active ? 'Yes' : 'Switched off'}</td>
                  {canManage && (
                    <td>
                      <RowActions
                        rowLabel={template.name}
                        primary={{
                          label: 'Edit',
                          disabled: busy,
                          onSelect: () => setEditingChecklist(template),
                        }}
                        more={[
                          {
                            label: 'Remove',
                            danger: true,
                            disabled: busy,
                            onSelect: () => void run(() => deleteChecklistTemplate(template.id)),
                          },
                        ]}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/*
        `!empty` was here, and had to go with the presets.
        When the register was empty an admin got the "add the starting sets"
        button instead, and these two were hidden behind it. With the presets
        gone that left an empty state telling somebody to write a template and
        no way to write one — the exact failure this codebase has now shipped
        six times. Writing the first one is the ONLY thing to do on an empty
        register, so the buttons belong there most of all.
      */}
      {canManage && (
        <div className="button-row">
          {!addingProject && !editingProject && (
            <button
              type="button"
              className="secondary inline"
              disabled={busy}
              onClick={() => setAddingProject(true)}
            >
              New project template
            </button>
          )}
          {!addingChecklist && !editingChecklist && (
            <button
              type="button"
              className="secondary inline"
              disabled={busy}
              onClick={() => setAddingChecklist(true)}
            >
              New checklist
            </button>
          )}
        </div>
      )}

      {(addingProject || editingProject) && (
        <ProjectTemplateForm
          key={editingProject?.id ?? 'new'}
          template={editingProject}
          busy={busy}
          onCancel={() => {
            setAddingProject(false);
            setEditingProject(null);
          }}
          onSubmit={async (input) => {
            const ok = await run(() =>
              editingProject
                ? updateProjectTemplate(editingProject.id, input)
                : createProjectTemplate(input as Parameters<typeof createProjectTemplate>[0]),
            );
            if (ok) {
              setAddingProject(false);
              setEditingProject(null);
            }
          }}
          onError={(message) => setError(message)}
        />
      )}

      {(addingChecklist || editingChecklist) && (
        <ChecklistTemplateForm
          key={editingChecklist?.id ?? 'new'}
          template={editingChecklist}
          busy={busy}
          onCancel={() => {
            setAddingChecklist(false);
            setEditingChecklist(null);
          }}
          onSubmit={async (input) => {
            const ok = await run(() =>
              editingChecklist
                ? updateChecklistTemplate(editingChecklist.id, input)
                : createChecklistTemplate(input as Parameters<typeof createChecklistTemplate>[0]),
            );
            if (ok) {
              setAddingChecklist(false);
              setEditingChecklist(null);
            }
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function ProjectTemplateForm({
  template,
  busy,
  onCancel,
  onSubmit,
  onError,
}: {
  readonly template: ProjectTemplate | null;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    name: string;
    project_type: string;
    is_active?: boolean;
    milestones: readonly { name: string; offset_days: number; is_critical: boolean }[];
  }) => void | Promise<void>;
  readonly onError: (message: string) => void;
}): JSX.Element {
  const [name, setName] = useState(template?.name ?? '');
  const [projectType, setProjectType] = useState(template?.project_type ?? 'office');
  const [active, setActive] = useState(template?.is_active ?? true);
  const [lines, setLines] = useState(template ? milestonesToLines(template) : '');

  return (
    <div className="panel">
      <p className="panel-title">{template ? `Edit ${template.name}` : 'New project template'}</p>
      <div className="field-row">
        <div className="field">
          {/* "Template name", not "Name". This form opens on the Company page,
              which already has a "Name" field for the company itself — two
              controls with the same accessible name on one screen is ambiguous
              to a screen reader before it is ambiguous to a test. */}
          <label htmlFor="pt-name">Template name</label>
          <input
            id="pt-name"
            value={name}
            disabled={busy}
            maxLength={160}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pt-type">Project type</label>
          <select
            id="pt-type"
            value={projectType}
            disabled={busy}
            onChange={(e) => setProjectType(e.target.value)}
          >
            {(PROJECT_TYPES as readonly string[]).includes(projectType)
              ? PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))
              : [projectType, ...PROJECT_TYPES].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
          </select>
        </div>
      </div>
      <div className="field">
        <label htmlFor="pt-milestones">Milestones — one per line: days, name (* = critical)</label>
        <textarea
          id="pt-milestones"
          value={lines}
          disabled={busy}
          rows={8}
          onChange={(e) => setLines(e.target.value)}
          placeholder={'14, Design sign-off *\n60, Ceilings closed *\n90, Handover *'}
        />
        <p className="hint">
          Days count from the project&rsquo;s start date when the template is applied.
        </p>
      </div>
      {template && (
        <div className="check">
          <input
            id="pt-active"
            type="checkbox"
            checked={active}
            disabled={busy}
            onChange={(e) => setActive(e.target.checked)}
          />
          <label htmlFor="pt-active">Offer this template for new projects</label>
        </div>
      )}
      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !name.trim() || !lines.trim()}
          onClick={() => {
            const parsed = parseMilestoneLines(lines);
            if (typeof parsed === 'string') {
              onError(parsed);
              return;
            }
            void onSubmit({
              name: name.trim(),
              project_type: projectType,
              ...(template ? { is_active: active } : {}),
              milestones: parsed,
            });
          }}
        >
          {busy ? 'Saving…' : 'Save template'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ChecklistTemplateForm({
  template,
  busy,
  onCancel,
  onSubmit,
}: {
  readonly template: ChecklistTemplate | null;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: {
    name: string;
    discipline: string;
    is_active?: boolean;
    items: readonly string[];
  }) => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(template?.name ?? '');
  const [discipline, setDiscipline] = useState(template?.discipline ?? 'other');
  const [active, setActive] = useState(template?.is_active ?? true);
  const [lines, setLines] = useState(template ? template.items.map((i) => i.text).join('\n') : '');

  const items = lines
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  return (
    <div className="panel">
      <p className="panel-title">{template ? `Edit ${template.name}` : 'New checklist'}</p>
      <div className="field-row">
        <div className="field">
          {/* "Checklist name", for the same reason as the template form above. */}
          <label htmlFor="ct-name">Checklist name</label>
          <input
            id="ct-name"
            value={name}
            disabled={busy}
            maxLength={160}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="ct-discipline">Discipline</label>
          <input
            id="ct-discipline"
            value={discipline}
            disabled={busy}
            maxLength={30}
            onChange={(e) => setDiscipline(e.target.value)}
            placeholder="architectural"
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="ct-items">Checklist lines — one per line</label>
        <textarea
          id="ct-items"
          value={lines}
          disabled={busy}
          rows={8}
          onChange={(e) => setLines(e.target.value)}
        />
      </div>
      {template && (
        <div className="check">
          <input
            id="ct-active"
            type="checkbox"
            checked={active}
            disabled={busy}
            onChange={(e) => setActive(e.target.checked)}
          />
          <label htmlFor="ct-active">Offer this checklist for inspections</label>
        </div>
      )}
      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !name.trim() || items.length === 0}
          onClick={() =>
            void onSubmit({
              name: name.trim(),
              discipline: discipline.trim() || 'other',
              ...(template ? { is_active: active } : {}),
              items,
            })
          }
        >
          {busy ? 'Saving…' : 'Save checklist'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
